import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaRda.api';
import './SchedaRda.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoRda = 'APERTA' | 'EVASA' | 'ANNULLATA';

interface RigaRda {
  id: number;
  ordinePos: number;
  articoloId: number | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number | string;
  quantitaOrdinata: number | string;
  note: string | null;
  rigaOrdineId: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  ordineId: number | null;
  commessaId: number | null;
  articolo: {
    id: number;
    codice: string;
    descrizioneBase: string;
    tipologia: string;
    fornitore: { id: number; ragioneSociale: string } | null;
  } | null;
  ordine: { id: number; numero: string } | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface Rda {
  id: number;
  societaId: number;
  numero: number;
  anno: number;
  etichetta: string;
  stato: StatoRda;
  note: string | null;
  createdAt: string;
  annullataAt: string | null;
  createdBy: { id: number; nome: string } | null;
  annullataDa: { id: number; nome: string } | null;
  righe: RigaRda[];
  logs: LogEntry[];
}

// Riga in editing locale (quantità come stringa per l'input)
interface RigaLocale {
  key: string;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  fornitore: string | null;
  unitaMisura: string | null;
  quantita: string;
  quantitaOrdinata: number;
  note: string;
  rigaOrdineId: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  ordineId: number | null;
  commessaId: number | null;
  ordineNumero: string | null;
  commessaNumero: string | null;
  commessaDescrizione: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_RDA: Record<StatoRda, { label: string; cls: string; title?: string }> = {
  APERTA:    { label: 'Aperta',    cls: 'bop-badge-info' },
  EVASA:     { label: 'Evasa',     cls: 'bop-badge-success', title: 'Tutte le righe sono state ordinate a fornitore' },
  ANNULLATA: { label: 'Annullata', cls: 'bop-badge-danger' },
};

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function rigaServerToLocale(r: RigaRda): RigaLocale {
  return {
    key: `srv-${r.id}`,
    articoloId: r.articoloId,
    codice: r.articolo?.codice ?? null,
    descrizione: r.descrizione,
    fornitore: r.articolo?.fornitore?.ragioneSociale ?? null,
    unitaMisura: r.unitaMisura,
    quantita: String(r.quantita ?? '').replace('.', ','),
    quantitaOrdinata: Number(r.quantitaOrdinata ?? 0) || 0,
    note: r.note || '',
    rigaOrdineId: r.rigaOrdineId,
    componenteId: r.componenteId,
    rigaDistintaId: r.rigaDistintaId,
    ordineId: r.ordineId,
    commessaId: r.commessaId,
    ordineNumero: r.ordine?.numero ?? null,
    commessaNumero: r.commessa?.numero ?? null,
    commessaDescrizione: r.commessa?.descrizione ?? null,
  };
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rda-scheda__campo">
      <span className="rda-scheda__campo-label">{label}</span>
      <span className="rda-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaRda() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [rda, setRda] = useState<Rda | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing note
  const [note, setNote] = useState('');
  const [noteSnapshot, setNoteSnapshot] = useState('');

  // Editing righe
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  const [righeSnapshot, setRigheSnapshot] = useState('');

  const [salvandoNote, setSalvandoNote] = useState(false);
  const [salvandoRighe, setSalvandoRighe] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const etichetta = rda?.etichetta || 'RDA';
  const isAperta = rda?.stato === 'APERTA';
  const canEdit = can('acquisti.rda', 'edit');
  const editabile = isAperta && canEdit;
  // Se anche una sola riga è già stata ordinata a fornitore, le righe non sono
  // più modificabili (il backend risponde 409 RIGHE_ORDINATE)
  const haRigheOrdinate = (rda?.righe || []).some(r => (Number(r.quantitaOrdinata) || 0) > 0);
  const righeEditabili = editabile && !haRigheOrdinate;

  useBloccoRecordContesto({
    recordSocietaId: rda?.societaId,
    label: etichetta,
  });

  const noteDirty = noteSnapshot !== '' && JSON.stringify(note) !== noteSnapshot;
  const righeDirty = righeSnapshot !== '' && JSON.stringify(righe) !== righeSnapshot;
  useDirtyForm(editabile && (noteDirty || righeDirty), etichetta);

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/rda/:id -> {"success":true,"data":{"id":"number","societaId":"number","numero":"number","anno":"number","etichetta":"string","stato":"APERTA|EVASA|ANNULLATA","note":"string|null","createdAt":"string","annullataAt":"string|null","createdBy":{"id":"number","nome":"string"},"annullataDa":{"id":"number","nome":"string"},"righe":[{"id":"number","ordinePos":"number","articoloId":"number|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","quantitaOrdinata":"number","note":"string|null","rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","ordineId":"number|null","commessaId":"number|null","articolo":{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string","fornitore":{"id":"number","ragioneSociale":"string"}},"ordine":{"id":"number","numero":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string"}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}]}}
      const r = await apiFetch<{ success: boolean; data?: Rda; error?: string }>(`/api/rda/${id}`);
      if (!r.success || !r.data) {
        setErrore(typeof r.error === 'string' ? r.error : 'RDA non trovata');
        setRda(null);
        return;
      }
      const d = r.data;
      setRda(d);
      setNote(d.note || '');
      setNoteSnapshot(JSON.stringify(d.note || ''));
      const righeLocali = (d.righe || []).map(rigaServerToLocale);
      setRighe(righeLocali);
      setRigheSnapshot(JSON.stringify(righeLocali));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento della RDA'));
      setRda(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const annulla = async () => {
    if (!rda) return;
    const motivo = window.prompt(
      `Annullare la ${etichetta}? Le quantità torneranno nel fabbisogno.\nMotivo (facoltativo):`
    );
    if (motivo === null) return; // utente ha premuto Annulla
    setAzioneInCorso(true);
    try {
      // @api POST /api/rda/:id/annulla {"motivo":"string|null"} -> {"success":true,"data":{"stato":"ANNULLATA"}}
      await apiFetch<{ success: boolean; data?: { stato: string } }>(
        `/api/rda/${rda.id}/annulla`,
        { method: 'POST', body: JSON.stringify({ motivo: motivo.trim() || null }) }
      );
      showToast(`${etichetta} annullata — le quantità tornano nel fabbisogno`, 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'annullo della RDA"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  // ── Salvataggio note ────────────────────────────────────────────────────────

  const salvaNote = async () => {
    if (!rda) return;
    setSalvandoNote(true);
    try {
      // @api PUT /api/rda/:id {"note":"string|null"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/rda/${rda.id}`, {
        method: 'PUT',
        body: JSON.stringify({ note: note.trim() || null }),
      });
      showToast('Note salvate', 'success');
      setNoteSnapshot(JSON.stringify(note));
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle note'), 'error');
    } finally {
      setSalvandoNote(false);
    }
  };

  // ── Editing righe ───────────────────────────────────────────────────────────

  const setCampoRiga = (key: string, campo: 'quantita' | 'note', val: string) => {
    setRighe(prev => prev.map(r => (r.key === key ? { ...r, [campo]: val } : r)));
  };

  const rimuoviRiga = (key: string) => {
    setRighe(prev => prev.filter(r => r.key !== key));
  };

  const salvaRighe = async () => {
    if (!rda) return;
    if (righe.length === 0) {
      showToast('La RDA deve avere almeno una riga. Per eliminarla tutta usa "Annulla RDA".', 'error');
      return;
    }
    const payload: Array<{
      articoloId: number | null;
      descrizione: string;
      unitaMisura: string | null;
      quantita: number;
      note: string | null;
      rigaOrdineId: number | null;
      componenteId: number | null;
      rigaDistintaId: number | null;
      ordineId: number | null;
      commessaId: number | null;
    }> = [];
    for (const r of righe) {
      const q = Number(r.quantita.replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per la riga "${r.descrizione}"`, 'error');
        return;
      }
      payload.push({
        articoloId: r.articoloId,
        descrizione: r.descrizione,
        unitaMisura: r.unitaMisura,
        quantita: q,
        note: r.note.trim() || null,
        rigaOrdineId: r.rigaOrdineId,
        componenteId: r.componenteId,
        rigaDistintaId: r.rigaDistintaId,
        ordineId: r.ordineId,
        commessaId: r.commessaId,
      });
    }
    setSalvandoRighe(true);
    try {
      // @api PUT /api/rda/:id {"righe":[{"articoloId":"number|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","note":"string|null","rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","ordineId":"number|null","commessaId":"number|null"}]} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/rda/${rda.id}`, {
        method: 'PUT',
        body: JSON.stringify({ righe: payload }),
      });
      showToast('Righe RDA salvate', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle righe'), 'error');
    } finally {
      setSalvandoRighe(false);
    }
  };

  const quantitaLetta = (r: RigaLocale): string => {
    const q = Number(r.quantita.replace(',', '.'));
    return Number.isFinite(q) ? formatQuantity(q) : r.quantita;
  };

  // Residuo da ordinare = quantità richiesta − quantità già ordinata
  const residuoRiga = (r: RigaLocale): number => {
    const q = Number(r.quantita.replace(',', '.'));
    return (Number.isFinite(q) ? q : 0) - r.quantitaOrdinata;
  };

  const statoBadge = rda ? (
    <span
      className={`bop-badge ${(STATO_RDA[rda.stato] ?? { cls: 'bop-badge-neutral' }).cls}`}
      title={(STATO_RDA[rda.stato] ?? {}).title}
    >
      {(STATO_RDA[rda.stato] ?? { label: rda.stato }).label}
    </span>
  ) : null;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="rda-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento RDA…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !rda) {
    return (
      <Layout>
        <div className="rda-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">RDA non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'RDA non trovata.'}</p>
              <BopBackLink to="/commerciale/passivo/rda" label="Torna alle RDA" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const d = rda;

  return (
    <Layout>
      <div className="rda-scheda">
        <BopBackLink to="/commerciale/passivo/rda" label="Torna alle RDA" />

        {/* Header */}
        <header className="rda-scheda__header">
          <div className="rda-scheda__header-left">
            <h1 className="bop-page-title">{etichetta}</h1>
            {statoBadge}
          </div>
          <div className="rda-scheda__header-actions">
            {isAperta && can('acquisti.ordini_fornitori', 'edit') && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => navigate('/commerciale/passivo/ordini/nuovi')}
                title="Componi gli ordini fornitore dalle righe RDA da ordinare"
              >
                Ordina a fornitore
              </button>
            )}
            {isAperta && canEdit && (
              <button
                className="bop-btn bop-btn-danger"
                onClick={annulla}
                disabled={azioneInCorso}
                title="Annulla la RDA — le quantità tornano nel fabbisogno"
              >
                Annulla RDA
              </button>
            )}
          </div>
        </header>

        {/* Informazioni */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Informazioni</h2>
            {editabile && noteDirty && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaNote}
                disabled={salvandoNote}
              >
                {salvandoNote ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            <div className="rda-scheda__grid">
              <Campo label="Stato" value={statoBadge} />
              <Campo
                label="Creata da / il"
                value={`${d.createdBy?.nome || '—'} · ${formatDateTimeIT(d.createdAt)}`}
              />
              <Campo
                label="Annullata da / il"
                value={
                  d.annullataAt
                    ? `${d.annullataDa?.nome || '—'} · ${formatDateTimeIT(d.annullataAt)}`
                    : null
                }
              />
            </div>
            {editabile ? (
              <div className="rda-scheda__note-group">
                <span className="rda-scheda__campo-label">Note</span>
                <textarea
                  className="bop-input rda-scheda__note-textarea"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Note per l'ufficio acquisti"
                  rows={3}
                />
              </div>
            ) : (
              <div className="rda-scheda__note-group">
                <Campo label="Note" value={d.note} />
              </div>
            )}
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
            {editabile && haRigheOrdinate && (
              <span className="bop-text-muted">
                Righe non modificabili: alcune quantità sono già state ordinate a fornitore.
              </span>
            )}
            {righeEditabili && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaRighe}
                disabled={salvandoRighe || !righeDirty}
              >
                {salvandoRighe ? 'Salvataggio…' : 'Salva righe'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Nessuna riga nella RDA.</p>
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Fornitore abituale</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Quantità</th>
                    <th className="bop-text-right">Ordinata</th>
                    <th className="bop-text-right">Residuo</th>
                    <th>Ordine</th>
                    <th>Commessa</th>
                    <th>Note</th>
                    {righeEditabili && <th className="bop-table__th--actions"></th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => (
                    <tr key={r.key}>
                      <td>{i + 1}</td>
                      <td>{r.codice || <span className="bop-text-muted">—</span>}</td>
                      <td>{r.descrizione}</td>
                      <td>{r.fornitore || <span className="bop-text-muted">—</span>}</td>
                      <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right">
                        {righeEditabili ? (
                          <input
                            className="bop-input rda-scheda__qta-input"
                            value={r.quantita}
                            onChange={e => setCampoRiga(r.key, 'quantita', e.target.value)}
                          />
                        ) : (
                          quantitaLetta(r)
                        )}
                      </td>
                      <td className="bop-text-right">{formatQuantity(r.quantitaOrdinata)}</td>
                      <td className="bop-text-right">
                        {residuoRiga(r) <= 0 ? (
                          <span className="bop-badge bop-badge-success">Ordinata</span>
                        ) : (
                          formatQuantity(residuoRiga(r))
                        )}
                      </td>
                      <td>
                        {r.ordineId && r.ordineNumero ? (
                          <Link to={`/commerciale/attivo/ordini/${r.ordineId}`}>{r.ordineNumero}</Link>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {r.commessaNumero ? (
                          <span
                            className="rda-scheda__commessa"
                            title={r.commessaDescrizione || undefined}
                          >
                            {r.commessaNumero}
                          </span>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {righeEditabili ? (
                          <input
                            className="bop-input rda-scheda__note-input"
                            value={r.note}
                            onChange={e => setCampoRiga(r.key, 'note', e.target.value)}
                            placeholder="Note"
                          />
                        ) : (
                          r.note || <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      {righeEditabili && (
                        <td className="bop-table__td--actions">
                          <button
                            className="bop-btn bop-btn-danger bop-btn-sm"
                            onClick={() => rimuoviRiga(r.key)}
                          >
                            Rimuovi
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(d.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {d.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
